// Layout Shift Prevention Utilities

/**
 * Creates a placeholder with fixed dimensions to prevent layout shifts
 * @param {number} width - Width in pixels
 * @param {number} height - Height in pixels
 * @param {string} backgroundColor - Background color (optional)
 * @returns {Object} Style object for placeholder
 */
export const createPlaceholder = (width, height, backgroundColor = '#f5f5f5') => ({
  width: `${width}px`,
  height: `${height}px`,
  backgroundColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  transition: 'opacity 0.3s ease'
});

/**
 * Preloads critical images to prevent layout shifts
 * @param {Array} imageUrls - Array of image URLs to preload
 * @returns {Promise} Promise that resolves when all images are loaded
 */
export const preloadImages = (imageUrls) => {
  return Promise.all(
    imageUrls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
      });
    })
  );
};

/**
 * Measures and reserves space for dynamic content
 * @param {HTMLElement} element - Element to measure
 * @returns {Object} Dimensions object
 */
export const reserveSpace = (element) => {
  if (!element) return { width: 0, height: 0 };
  
  const rect = element.getBoundingClientRect();
  return {
    width: rect.width,
    height: rect.height
  };
};

/**
 * Creates a skeleton loader with proper dimensions
 * @param {Object} options - Configuration options
 * @returns {Object} Skeleton configuration
 */
export const createSkeleton = ({
  width = '100%',
  height = 200,
  variant = 'rectangular',
  animation = 'pulse'
}) => ({
  width,
  height,
  variant,
  animation,
  sx: {
    bgcolor: 'grey.100',
    borderRadius: 1
  }
});

/**
 * Font loading optimization to prevent FOIT/FOUT
 */
export const optimizeFontLoading = () => {
  // Check if fonts are loaded
  if ('fonts' in document) {
    document.fonts.ready.then(() => {
      // All fonts loaded
      document.body.classList.add('fonts-loaded');
    });
    
    // Load critical fonts with timeout
    const criticalFonts = [
      'Poppins',
      'Montserrat'
    ];
    
    criticalFonts.forEach(fontFamily => {
      const font = new FontFace(fontFamily, `url(/fonts/${fontFamily}.woff2)`);
      font.load().then(() => {
        document.fonts.add(font);
      }).catch(err => {
        // Font loading failed silently
      });
    });
  }
};

/**
 * Intersection Observer for lazy loading with proper placeholders
 * @param {HTMLElement} target - Target element
 * @param {Function} callback - Callback function
 * @param {Object} options - Observer options
 */
export const createIntersectionObserver = (target, callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };
  
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, defaultOptions);
  
  if (target) {
    observer.observe(target);
  }
  
  return observer;
};

/**
 * Prevents layout shift during image loading
 * @param {string} src - Image source
 * @param {number} aspectRatio - Image aspect ratio (width/height)
 * @returns {Object} Image container styles
 */
export const preventImageLayoutShift = (src, aspectRatio = 16/9) => {
  return {
    position: 'relative',
    width: '100%',
    paddingBottom: `${(1 / aspectRatio) * 100}%`,
    overflow: 'hidden',
    backgroundColor: '#f5f5f5',
    '& img': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }
  };
};

/**
 * Web Vitals monitoring for CLS - DISABLED to prevent console spam
 */
export const monitorCLS = () => {
  // Temporarily disabled to prevent infinite console logging
  // This can be re-enabled for debugging purposes only
  // CLS monitoring disabled to prevent console spam
};

/**
 * Critical resource hints for better performance
 */
export const addResourceHints = () => {
  const head = document.head;
  
  // Preconnect to external domains
  const preconnectDomains = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ];
  
  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    head.appendChild(link);
  });
  
  // Preload critical resources
  const criticalResources = [
    { href: '/Logos/logoFocus.png', as: 'image', type: 'image/png' }
  ];
  
  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.href;
    link.as = resource.as;
    if (resource.type) link.type = resource.type;
    head.appendChild(link);
  });
};

// Initialize layout shift prevention on page load
export const initializeLayoutShiftPrevention = () => {
  // Add fonts-loading class to prevent FOIT
  document.documentElement.classList.add('fonts-loading');
  
  // Optimize font loading
  optimizeFontLoading();
  
  // Add resource hints
  addResourceHints();
  
  // Monitor CLS
  monitorCLS();
  
  // Remove fonts-loading class when fonts are ready
  if ('fonts' in document) {
    document.fonts.ready.then(() => {
      document.documentElement.classList.remove('fonts-loading');
      document.documentElement.classList.add('fonts-loaded');
    });
  }
};